import { PitchDesignHelper } from 'classes/helpers/pitch-design.helper';
import { DEFAULT_INPUTS, STANDARD_PY_FT } from 'enums/pitch-design';
import { IPitchDesignInputs } from 'interfaces/i-pitch-design';
import { BallHelper } from 'lib_ts/classes/ball.helper';
import { MachineHelper } from 'lib_ts/classes/machine.helper';
import { TrajHelper } from 'lib_ts/classes/trajectory.helper';
import { BuildPriority } from 'lib_ts/enums/pitches.enums';
import { IBallChar } from 'lib_ts/interfaces/i-ball-char';
import {
  DEFAULT_PITCH,
  DEFAULT_PLATE,
  IBallState,
  IPitch,
  ITrajectory,
} from 'lib_ts/interfaces/pitches';
import { DEFAULT_SEAMS, ISpin } from 'lib_ts/interfaces/pitches/i-base';
import { clone, cloneDeep } from 'lodash';
import { create } from 'zustand';

type State = {
  isNew: boolean;
  showCurrent: boolean;
  priority: BuildPriority;

  refPitch: IPitch; // * serves as reference point for current/reset
  refBall: IBallChar; // * serves as reference point for current/reset

  inputs: IPitchDesignInputs; // changing these will update the bs and traj

  // tied to most recent derived bs
  bs_hash: string;
  derivedBs: IBallState;
  derivedTraj: ITrajectory;
};

type Actions = {
  setPriority: (v: BuildPriority) => void; // when a pitch has its own priority different from the app value
  setRefPitch: (v: IPitch) => void;
  mergeInputs: (v: Partial<IPitchDesignInputs>) => void;
  resetInputs: (priority: BuildPriority) => void;
};

type PitchDesignStore = State & Actions;

export const usePitchDesignStore = create<PitchDesignStore>((set) => {
  const defaultPriority = BuildPriority.Default;

  const mergeInputs = (v: Partial<IPitchDesignInputs>) => {
    set((state) => {
      const nextInputs: IPitchDesignInputs = {
        ...state.inputs,
        ...v,
      };

      const derived = PitchDesignHelper.deriveBsAndTraj(
        nextInputs,
        state.priority
      );

      return {
        // what the user changed
        inputs: nextInputs,

        // auto-update everything else
        derivedBs: derived.bs,
        bs_hash: MachineHelper.getBsHash(derived.bs, state.priority),
        derivedTraj: derived.traj,
      };
    });
  };

  const setRefPitch = (nextPitch: IPitch) => {
    const nextPriority = nextPitch.priority ?? defaultPriority;

    const nextBall = BallHelper.getCharsFromPitch(nextPitch);

    const nextInputs: IPitchDesignInputs = {
      plate:
        // if the pitch has a saved plate
        nextPitch.plate_loc_backup ??
        // guess the plate from a saved traj
        TrajHelper.getPlateLoc(nextPitch.traj) ??
        // fallback to middle-middle
        DEFAULT_PLATE,
      // don't allow 0 speed
      speedMph: Math.max(nextBall.speed, 1e-3),
      spins: {
        wx: nextBall.wx,
        wy: nextBall.wy,
        wz: nextBall.wz,
      },
      spinExt: {
        wnet: nextBall.wnet,
        gyro_angle: nextBall.gyro_angle,
        waxis: nextBall.waxis,
      },
      breaks: clone(nextBall.breaks) ?? TrajHelper.getBreaks(nextPitch.traj),
      seams: {
        latitude_deg: nextBall.latitude_deg,
        longitude_deg: nextBall.longitude_deg,
      },
      releasePos: {
        px: nextBall.px,
        py: nextBall.py,
        pz: nextBall.pz,
      },
      videoIDs: {
        stretchVideoID: nextPitch.stretchVideoID,
        windupVideoID: nextPitch.windupVideoID,
      },
    };

    const derived = PitchDesignHelper.deriveBsAndTraj(nextInputs, nextPriority);

    set({
      // from input
      refPitch: nextPitch,
      priority: nextPriority, // ensures the UI renders w/ the priority saved on pitch (not necessarily the same as app setting)

      // derived
      refBall: nextBall,
      inputs: nextInputs,
      derivedBs: derived.bs,
      derivedTraj: derived.traj,
      bs_hash: MachineHelper.getBsHash(derived.bs, nextPriority),
    });
  };

  const defaultDerived = PitchDesignHelper.deriveBsAndTraj(
    DEFAULT_INPUTS,
    defaultPriority
  );

  return {
    isNew: false,
    showCurrent: false,
    priority: defaultPriority,

    refPitch: cloneDeep(DEFAULT_PITCH),
    refBall: BallHelper.getCharsFromPitch(DEFAULT_PITCH),

    inputs: cloneDeep(DEFAULT_INPUTS),

    derivedBs: defaultDerived.bs,
    bs_hash: MachineHelper.getBsHash(defaultDerived.bs, defaultPriority),
    derivedTraj: defaultDerived.traj,

    setPriority: (nextPriority: BuildPriority) => {
      set((s) => {
        // b/c priority will affect derivation, we need to update the derived bs and traj values in case they get saved
        const derived = PitchDesignHelper.deriveBsAndTraj(
          s.inputs,
          nextPriority
        );

        return {
          priority: nextPriority,
          derivedBs: derived.bs,
          bs_hash: MachineHelper.getBsHash(derived.bs, nextPriority),
          derivedTraj: derived.traj,
        };
      });
    },
    setRefPitch: setRefPitch,
    mergeInputs: mergeInputs,

    resetInputs: (priority) => {
      // ensure that the priority of the UI matches the app priority
      set({
        priority: priority,
        isNew: true,
        showCurrent: false,
      });

      const defaultSpins: ISpin = {
        wx: -1500, // ? so that landing on default and switching to breaks gives 19.5" vertical break
        wy: 0,
        wz: 0,
      };

      const defaultPitch = PitchDesignHelper.deriveBsAndTraj(
        {
          speedMph: 85,
          spins: defaultSpins,
          spinExt: BallHelper.convertSpinToSpinExt(defaultSpins),
          breaks: {
            xInches: 0,
            zInches: 19.455, // ? so that landing on breaks and switching to default gives 1500 wx
          },
          seams: DEFAULT_SEAMS,
          plate: DEFAULT_PLATE,
          releasePos: {
            px: 0,
            py: STANDARD_PY_FT,
            pz: 4.5,
          },
          videoIDs: {
            stretchVideoID: null,
            windupVideoID: null,
          },
        },
        priority
      );

      // rebuild everything based on the default pitch
      setRefPitch({
        ...DEFAULT_PITCH,
        bs: defaultPitch.bs,
        traj: defaultPitch.traj,
        bs_hash: MachineHelper.getBsHash(defaultPitch.bs, priority),
        priority: priority,
      });
    },
  };
});
